import { computed } from 'vue';
import { useStore } from 'vuex';
import { useDashboard } from '@rdrive/plugin-dashboard-shell';
import { useAnalytics } from '@/utils/useAnalytics';

/**
 * THE bridge module pages read app context through, so they repeat neither the
 * `computed(() => store.getters.x)` / `store.state.x` lines nor the platform wiring
 * (project-overview's `composables/useAppContext.js`, adapted; since 2026-10-12). Package
 * components never call it — they take props.
 *
 * Two sources:
 *  - the PLATFORM half — the DuckDB connection and service, the REST client, the data loader,
 *    the URL settings (and their locale), `dataLoaded`, the two filter contexts, analytics —
 *    from the dashboard (`useDashboard()`, @rdrive/plugin-dashboard-shell);
 *  - the DOMAIN half — the status templates, the theme's chart font colour, the logo, build
 *    mode — from the Vuex store. This is the only composable that calls `useStore()`, so when
 *    Vuex is retired only this file changes. Pages still commit the store's filter resets
 *    themselves (`resetFilters`, `resetStatusHistoryFilters`), as project-overview's do.
 *
 * Semantics preserved from the call sites it replaced. The store's compat delegates the pages
 * read until 2026-10-12 (`getDBConnection`, `getDuckDBService`, `state.settings`, `getUserId`,
 * `currentLocale`, `getDataLoaded`, `state.filterContext`, `state.filterStatusHistoryContext`)
 * WERE these objects, never copies: the dashboard's services, loader and contexts are markRaw.
 * Nothing read those delegates once the pages came here, so the store dropped them on
 * 2026-10-13 (it keeps only `state.filterContext`/`filterStatusHistoryContext`, for the pages'
 * filter-reset commits).
 *  - `conn`, `duckdb`, `apiClient` and `loader` are NOT computed. Pages read them once at
 *    setup, as they always did (the store's old `getDBConnection`). They are
 *    stable objects (DuckDBService builds its gated `conn` once), and adapters unref whatever
 *    they are given.
 *  - `apiHost` is `settings.apiUrl` with a leading `api-` stripped — the web app's host
 *    (`useIssuesGrid` derives the grid's own the same way).
 *  - `dataLoaded` is the GLOBAL flag (the loader's). AnalysisPage has its own load lifecycle
 *    (a local `dataLoaded` that flips after its date-range query) and reads this one as
 *    `issuesLoaded`.
 *  - the filter contexts are computeds over the dashboard's `filters` (`issues`,
 *    `statusHistory` — dashboard.config.js), never captured values, so a context the dashboard
 *    replaces reaches every reader. `filterIssuesContext` is the context the store calls
 *    `state.filterContext` and the pages' templates `filterContext`.
 */
export function useAppContext() {
  const store = useStore();
  const dashboard = useDashboard();
  const { trackEvent } = useAnalytics();

  const settings = computed(() => dashboard.settings);

  const apiHost = computed(() => {
    const url = dashboard.settings.apiUrl || '';
    return url.startsWith('api-') ? url.slice(4) : url;
  });

  return {
    // Stable singletons — read once, like the pages already do
    conn: dashboard.conn,
    duckdb: dashboard.duckdb,
    apiClient: dashboard.api,
    loader: dashboard.loader,

    // Reactive values — platform
    settings,
    userId: computed(() => dashboard.settings.userId),
    projectCode: computed(() => dashboard.settings.projectCode),
    projectName: computed(() => dashboard.settings.projectName),
    locale: computed(() => dashboard.settings.locale),
    apiHost,
    dataLoaded: computed(() => dashboard.dataLoaded.value),

    // Reactive values — domain (the store)
    logoUrl: computed(() => store.getters.getLogoUrl),
    chartFontColor: computed(() => store.getters.chartFontColor),
    statusTemplates: computed(() => store.getters.getStatusTemplatesObj),
    buildMode: computed(() => store.state.buildMode),

    // The dashboard's filter contexts
    filterIssuesContext: computed(() => dashboard.filters.issues),
    filterStatusHistoryContext: computed(() => dashboard.filters.statusHistory),

    trackEvent
  };
}
